import {
  Icon,
  ICONS_ENUM,
  CircularLoader,
  Button,
  BUTTON_SIZE
} from '@sanlam-glacier/app-common/components';
import { useConfig } from 'providers/config/ConfigProvider';
import devDumpStyles from './dev-dump.module.scss';
import { useAtomValue } from 'jotai';
import { logHistoryAtom } from 'stores/jotai.store';
import { useUserContext } from 'providers/managers/user-context-manager/user-context-manager';
import { AuthenticatedUser, IdentificationState, useAuth } from 'auth';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import {
  formatDateAsString,
  formatDateTimeAsString
} from 'utils/string-handling/human-readable-text';
import { UserWithFeatures } from 'types/auth/user-context.type';
import { SecurityAccessFeaturesEnum } from 'bff';
import { Env } from 'types';
import { downloadBlob } from '@sanlam-glacier/app-common/utils';
import { useState } from 'react';
import { displayToast } from '@sanlam-glacier/app-common/modules/toasts';
import { LogMessageWithMeta } from 'types/logging/meta-log.type';

export const DevToolsDump = () => {
  const [loading, setLoading] = useState(false); // Add loading state
  const env = useConfig();
  const logHistory = useAtomValue(logHistoryAtom);
  const { getActingAsIntermediaryUser, getCurrentAuthenticatedUserWithFeatures, getUserFeatures } =
    useUserContext();
  const { getSignedInUser, identificationState } = useAuth();
  const actingAsUser = getActingAsIntermediaryUser();
  const currentUser = getCurrentAuthenticatedUserWithFeatures();
  const features = getUserFeatures();
  const signedInUser = getSignedInUser();
  const currentDateTime = formatDateTimeAsString(new Date());
  const date = formatDateAsString(new Date());

  const proposalNumber = window.location.href.split('/').filter(Boolean).pop();

  const getEnvironmentTypeUrl = (url: string) => {
    const coza = '.co.za';
    const splitArray = url.split('/');
    const cozaIndex = url.indexOf(coza) + coza.length;
    const scheme = splitArray[0];
    const domainName = splitArray[2];

    if (domainName?.includes('localhost')) return `${scheme}//${domainName}`;

    if (domainName?.includes('.co.za')) return url.substring(0, cozaIndex);

    return `${scheme}//${domainName}`;
  };

  const generateDumpInfo = (
    proposalNumber: string | undefined,
    currentUser: UserWithFeatures | undefined,
    signedInUser: AuthenticatedUser | undefined,
    actingAsUser: UserWithFeatures | undefined,
    logHistory: LogMessageWithMeta[],
    identificationState: IdentificationState,
    features: SecurityAccessFeaturesEnum[],
    env: Env
  ) => {
    return {
      'Proposal Number': proposalNumber,
      'Deployed Environment URL': getEnvironmentTypeUrl(window.location.href),
      'BFF URL': import.meta.env.VITE_API_URL,
      'Version Id': env.versionId,
      Date: currentDateTime,
      'Identification State': identificationState,
      User:
        currentUser?.firstName && currentUser?.lastName
          ? `${currentUser?.firstName} ${currentUser?.lastName}`
          : undefined,
      'User email': signedInUser?.email,
      'User Code': currentUser?.intermediaryCode,
      'Acting as User':
        actingAsUser?.firstName && actingAsUser?.lastName
          ? `${actingAsUser?.firstName} ${actingAsUser?.lastName}`
          : undefined,
      'Acting as User Code': actingAsUser?.intermediaryCode,
      'User Features': currentUser?.features,
      'Acting as User Features': actingAsUser?.features,
      'Active Feature Set': features,
      'Gateway Token': signedInUser?.accessToken,
      'Log History': logHistory
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 10),
      Environment: env
    };
  };

  const dumpInfo = generateDumpInfo(
    proposalNumber,
    currentUser,
    signedInUser,
    actingAsUser,
    logHistory,
    identificationState,
    features,
    env
  );

  const copyDump = async () => {
    await navigator.clipboard.writeText(JSON.stringify(dumpInfo, null, 2));
    displayToast({
      toastId: 'dev-snapshot-copy',
      title: 'Snapshot Copied',
      message: 'The snapshot was copied to the clipboard',
      status: 'info'
    });
  };

  const downloadDump = async () => {
    setLoading(true);
    try {
      const zip = new JSZip();

      const canvas = await html2canvas(document.body);
      const screenshotDataUrl = canvas.toDataURL('image/png');

      // Extract the base64-encoded image data from the Data URL. https://developer.mozilla.org/en-US/docs/Web/URI/Schemes/data
      const base64Image = screenshotDataUrl.split(',')[1];
      const jsonString = JSON.stringify(dumpInfo, null, 2);

      zip.file('snapshot-info.json', jsonString);
      if (base64Image) zip.file(`ghub-screenshot-${date}.png`, base64Image, { base64: true });

      const content = await zip.generateAsync({ type: 'blob' });
      downloadBlob(content, `snapshot-${currentDateTime}.zip`);

      displayToast({
        toastId: 'dev-snapshot-download',
        title: 'Snapshot Downloaded',
        message: 'The snapshot has been downloaded as a ZIP file',
        status: 'info'
      });
    } catch (error) {
      logger.error({
        message: 'Error capturing screenshot or downloading ZIP:',
        object: error ?? {}
      });
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  return (
    <>
      <div className={devDumpStyles.devDumpInfoText}>
        <div>
          <Icon icon={ICONS_ENUM.ACTION_INFO} />
        </div>
        <div className={devDumpStyles.devDumpInfoTextMargin}>
          Snapshot and creates a zip file which includes a JSON snapshot of all contextual
          information aswell as a screenshot of the current screen. You can attach this zip file to
          user stories or bugs to assist with debugging.
        </div>
      </div>
      <div className={devDumpStyles.devDumpInfoButtons}>
        <Button
          endIcon={<Icon icon={ICONS_ENUM.CONTENT_COPY} />}
          color={Button.COLOR.ERROR}
          size={BUTTON_SIZE.SMALL}
          onClick={copyDump}
        >
          Copy Snapshot
        </Button>
        <Button
          endIcon={!loading && <Icon icon={ICONS_ENUM.FILE_DOWNLOAD} />}
          color={Button.COLOR.ERROR}
          size={BUTTON_SIZE.SMALL}
          onClick={downloadDump}
          disabled={loading}
          loading={loading}
        >
          {loading ? <CircularLoader thickness={3} /> : 'Download Snapshot'}
        </Button>
      </div>
    </>
  );
};
